import { useEffect, useRef } from 'react';
import { useNow } from './useNow.js';
import { usePrefersReducedMotion } from './usePrefersReducedMotion.js';
import { COUNTDOWN_HOUR_MS, COUNTDOWN_MINUTE_MS, COUNTDOWN_SECOND_MS, countdownTickMs } from '../_helpers/countdown.js';

export { COUNTDOWN_HOUR_MS, COUNTDOWN_MINUTE_MS, COUNTDOWN_SECOND_MS };

const toMs = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const ms = value instanceof Date ? value.getTime() : typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

/**
 * A live countdown to `endsAt` (an RFC 3339 string, a Date or ms; null for
 * none), shared by the sale price (ENG-832) and time-limited items (ENG-834):
 *
 *   { endsAtMs, remainingMs, ended, seconds }
 *
 * It ticks on whole minutes while more than `secondsWithinMs` is left (an
 * hour by default; a day for an item leaving the shop, ENG-876), and on whole
 * seconds after that (`seconds` true) unless the device asks for reduced
 * motion (`hooks/usePrefersReducedMotion.js`), which keeps it on minutes so
 * nothing changes every second. It always flips to `ended` exactly at
 * `endsAt` (an exclusive end). No timer runs without an `endsAt`.
 *
 * `onEnd()` is called once the end is reached, also when it had already
 * passed when the countdown started. With `repeatMs`, it is called again every
 * `repeatMs` while still ended, at most `maxRepeats` more times per `endsAt`:
 * for a re-read that still carries the old end (the device clock a little
 * ahead of the server's). A new `endsAt` starts the count again.
 *
 * The remaining time is always `endsAt` minus the device clock at the last
 * tick (useNow catches up at once when a background tab is shown again),
 * never a count of ticks, so throttled timers cannot make it drift.
 */
export const useCountdown = (endsAt, { onEnd = null, repeatMs = 0, maxRepeats = 0, secondsWithinMs = COUNTDOWN_HOUR_MS } = {}) => {
  const endsAtMs = toMs(endsAt);
  const reducedMotion = usePrefersReducedMotion();
  const now = useNow((current) => countdownTickMs(endsAtMs - current, { seconds: !reducedMotion, secondsWithinMs }), {
    enabled: endsAtMs !== null,
    until: endsAtMs,
  });
  const remainingMs = endsAtMs === null ? null : Math.max(0, endsAtMs - now);
  const ended = endsAtMs !== null && now >= endsAtMs;

  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;
  // How often onEnd has been called for which end, across renders.
  const firedRef = useRef({ endsAtMs: null, count: 0, lastAt: 0 });

  useEffect(() => {
    if (!ended || endsAtMs === null) return undefined;
    if (firedRef.current.endsAtMs !== endsAtMs) firedRef.current = { endsAtMs, count: 0, lastAt: 0 };
    const record = firedRef.current;
    const limit = 1 + Math.max(0, maxRepeats);
    let timer = null;
    const fire = () => {
      timer = null;
      if (record.count >= limit) return;
      record.count += 1;
      record.lastAt = Date.now();
      onEndRef.current?.();
      if (record.count < limit && repeatMs > 0) timer = setTimeout(fire, repeatMs);
    };
    if (record.count === 0) fire();
    else if (record.count < limit && repeatMs > 0) {
      timer = setTimeout(fire, Math.max(0, record.lastAt + repeatMs - Date.now()));
    }
    return () => { if (timer) clearTimeout(timer); };
  }, [ended, endsAtMs, repeatMs, maxRepeats]);

  return { endsAtMs, remainingMs, ended, seconds: !reducedMotion && remainingMs !== null && remainingMs <= secondsWithinMs };
};

export default useCountdown;
